import { useCallback, useEffect, useRef, useState } from "react";
import { X, Eraser, CheckCircle, PlugsConnected, Plug, PenNib } from "@phosphor-icons/react";
import { toast } from "sonner";
import { WacomStu, webHidSupported } from "../lib/wacomStu";
import { paintSigningScreen } from "../lib/signingScreen";

const W = 720, H = 432;

export function SignatureCapture({ open, title = "Customer signature", agreement, signerName, amount, amountLabel = "Payout", heading = "Trade-in agreement", onCancel, onAccept }) {
  const canvasRef = useRef(null);
  const padRef = useRef(null);
  const last = useRef(null);
  const [hasInk, setHasInk] = useState(false);
  const [pad, setPad] = useState(null);
  const [padLoading, setPadLoading] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const ink = useRef(null);
  const box = useRef(null);
  const content = { heading, amountLabel, amount, text: agreement, signer: signerName };
  const contentRef = useRef(content);
  contentRef.current = content;

  const ctx = () => canvasRef.current?.getContext("2d");
  const reset = useCallback(() => {
    const c = ctx(); if (!c) return;
    paintSigningScreen(c, W, H, contentRef.current);
    ink.current = Object.assign(document.createElement("canvas"), { width: W, height: H });
    box.current = { x1: W, y1: H, x2: 0, y2: 0 };
    last.current = null; setHasInk(false);
  }, []);

  const pushToPad = useCallback(async (dev) => {
    setPadLoading(true);
    try { await dev.showImage((g, w, h, colour) => paintSigningScreen(g, w, h, contentRef.current, colour)); }
    finally { setPadLoading(false); }
  }, []);

  const stroke = useCallback((x, y, down, press = 512) => {
    const c = ctx(); if (!c) return;
    if (!down) { last.current = null; return; }
    const p = { x, y };
    if (last.current) {
      const lw = 1.6 + Math.min(1, press / 900) * 2.4;
      [c, ink.current.getContext("2d")].forEach((g) => {
        g.strokeStyle = "#0F172A"; g.lineCap = "round"; g.lineJoin = "round"; g.lineWidth = lw;
        g.beginPath(); g.moveTo(last.current.x, last.current.y); g.lineTo(p.x, p.y); g.stroke();
      });
      const b = box.current;
      box.current = { x1: Math.min(b.x1, x, last.current.x), y1: Math.min(b.y1, y, last.current.y), x2: Math.max(b.x2, x, last.current.x), y2: Math.max(b.y2, y, last.current.y) };
      setHasInk(true);
    }
    last.current = p;
  }, []);

  const attach = useCallback((dev) => {
    dev.onPen = (e) => stroke(e.x * W, e.y * H, e.down, e.press);
    padRef.current = dev; setPad(dev.name);
    pushToPad(dev);
  }, [stroke, pushToPad]);

  useEffect(() => {
    if (!open) return;
    reset();
    const dev = new WacomStu();
    dev.reconnect().then((ok) => ok && attach(dev)).catch(() => {});
    return () => { padRef.current?.close(); padRef.current = null; setPad(null); };
  }, [open, reset, attach]);

  const connect = async () => {
    setConnecting(true);
    try {
      const dev = new WacomStu();
      if (await dev.connect()) { attach(dev); toast.success(`${dev.name} connected`, { description: "Customer can sign on the pad now" }); }
    } catch (e) { toast.error("Couldn't connect to the pad", { description: e.message }); }
    finally { setConnecting(false); }
  };

  const clear = async () => { reset(); if (padRef.current) await pushToPad(padRef.current); };

  const pos = (e) => { const r = canvasRef.current.getBoundingClientRect(); return [(e.clientX - r.left) * (W / r.width), (e.clientY - r.top) * (H / r.height)]; };
  const onPointer = (down) => (e) => {
    if (down === "move" && !(e.buttons & 1)) return;
    if (down === true) e.currentTarget.setPointerCapture(e.pointerId);
    const [x, y] = pos(e);
    stroke(x, y, down !== false, e.pressure ? e.pressure * 1000 : 512);
  };

  const accept = () => {
    if (!hasInk) { toast.error("Please sign first"); return; }
    const m = 12, b = box.current;
    const x = Math.max(0, b.x1 - m), y = Math.max(0, b.y1 - m);
    const w = Math.min(W, b.x2 + m) - x, h = Math.min(H, b.y2 + m) - y;
    const out = Object.assign(document.createElement("canvas"), { width: w, height: h });
    out.getContext("2d").drawImage(ink.current, x, y, w, h, 0, 0, w, h);
    onAccept({ png: out.toDataURL("image/png"), method: padRef.current ? "wacom_stu" : "screen" });
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[300] bg-slate-950/60 backdrop-blur-sm flex items-center justify-center p-4" data-testid="signature-modal">
      <div className="w-full max-w-[760px] rounded-2xl bg-white shadow-2xl overflow-hidden">
        <div className="px-6 py-4 flex items-center gap-3 border-b border-slate-100">
          <PenNib size={20} weight="bold" className="text-[#1E5FEB]" />
          <div className="flex-1">
            <div className="text-[16px] font-bold text-slate-900">{title}</div>
            {signerName && <div className="text-[12px] text-slate-500">Signing as <b className="text-slate-700">{signerName}</b></div>}
          </div>
          <button onClick={onCancel} className="w-9 h-9 rounded-lg hover:bg-slate-100 flex items-center justify-center" data-testid="signature-cancel"><X size={16} weight="bold" /></button>
        </div>
        {agreement && <span className="sr-only" data-testid="signature-agreement">{agreement} {amount}</span>}
        <div className="px-6 pt-4">
          <canvas ref={canvasRef} width={W} height={H} data-testid="signature-canvas"
            className="w-full aspect-[5/3] rounded-xl border-2 border-dashed border-slate-200 touch-none cursor-crosshair bg-white"
            onPointerDown={onPointer(true)} onPointerMove={onPointer("move")} onPointerUp={onPointer(false)} onPointerLeave={onPointer(false)} />
        </div>
        <div className="px-6 py-4 flex flex-wrap items-center gap-2">
          {pad ? (
            <span className="h-9 px-3 rounded-lg bg-emerald-50 border border-emerald-200 text-emerald-700 text-[12.5px] font-semibold inline-flex items-center gap-1.5" data-testid="signature-pad-status">
              <PlugsConnected size={15} weight="bold" />{pad} {padLoading ? "· showing agreement on pad…" : "ready · sign on the pad"}
            </span>
          ) : webHidSupported() ? (
            <button onClick={connect} disabled={connecting} data-testid="signature-connect-pad"
              className="h-9 px-3 rounded-lg border border-[#BFDBFE] bg-[#EFF6FF] text-[#1E5FEB] text-[12.5px] font-semibold inline-flex items-center gap-1.5 hover:bg-[#DBEAFE] transition-colors">
              <Plug size={15} weight="bold" />{connecting ? "Connecting…" : "Connect Wacom pad"}
            </button>
          ) : (
            <span className="text-[11.5px] text-slate-500" data-testid="signature-pad-unsupported">Use Chrome or Edge to sign on the Wacom pad. You can also sign above with a mouse or touch.</span>
          )}
          <div className="flex-1" />
          <button onClick={clear} data-testid="signature-clear" className="h-10 px-4 rounded-full border border-slate-200 text-[13px] font-semibold text-slate-700 inline-flex items-center gap-1.5 hover:bg-slate-50 transition-colors"><Eraser size={15} weight="bold" />Clear</button>
          <button onClick={accept} data-testid="signature-accept" disabled={!hasInk}
            className="h-10 px-5 rounded-full bg-[#1E5FEB] hover:bg-[#1849C8] disabled:bg-slate-200 disabled:text-slate-400 text-white text-[13px] font-bold inline-flex items-center gap-1.5 transition-colors"><CheckCircle size={16} weight="bold" />Accept signature</button>
        </div>
      </div>
    </div>
  );
}
